// Los huecos de la ficha 360 del cliente. Cada uno pinta SU motivo corto y guarda la explicación
// larga en el `title`: dos huecos con la misma etiqueta no dicen cuál de los dos falta.

/** Un hueco: la etiqueta corta que se PINTA y la explicación larga que va en el `title`. */
export interface Gap {
    /** Lo que se lee en la celda. Es un motivo, nunca un valor: «Pendiente» se leería como dato. */
    readonly label: string;
    readonly note: string;
}

export function isGap(value: unknown): value is Gap {
    if (typeof value !== 'object' || value === null) return false;
    const candidate = value as Partial<Gap>;
    return typeof candidate.label === 'string' && typeof candidate.note === 'string';
}

/* ================================================================================
   VERIFICACIONES DE KYC — la prueba de vida y el comprobante viven en las sesiones del wizard.
   ================================================================================ */

/**
 * El negocio del cliente no viaja en el listado de crédito: el BFF entrega la fila ya cruzada y no
 * publica de qué cadena es. Las lecturas que lo exigen no llegan a salir, y lo dicen.
 */
export const NO_RETAILER_IN_LIST: Gap = {
    label: 'No viaja en el listado',
    note: 'El listado de clientes de crédito no dice de qué negocio es cada cliente, así que esta ficha no lo tiene con qué resolver. No es que el cliente no pertenezca a ninguno.',
};

/** El nombre de la tienda de alta se pide en la cadena de la venta, y el crédito no la trae. */
export const ORIGIN_STORE_NO_SCOPE: Gap = {
    label: 'Sin cadena: no se resuelve',
    note: 'El nombre de la tienda se pide dentro de la cadena donde se hizo la venta, y el crédito de este cliente no la trae o no se pudo leer, así que la lectura no salió. No es que la tienda no exista.',
};

/** Las sesiones se piden en la cadena de la venta, y el crédito no la trae. */
export const KYC_SESSIONS_NO_SCOPE: Gap = {
    label: 'Sin cadena: no se piden',
    note: 'Las verificaciones se piden dentro de la cadena donde se hizo la venta, y el crédito de este cliente no la trae o no se pudo leer, así que la lectura no salió. No es que el cliente no tenga verificaciones.',
};

/** La lectura salió bien y el cliente no tiene ni una sesión de venta en esta cadena. */
export const NO_KYC_SESSIONS: Gap = {
    label: 'Sin sesión de verificación',
    note: 'La lectura salió bien y este cliente no tiene ninguna sesión de venta en este negocio: las verificaciones de MetaMap se corren dentro de una venta, así que no hay ninguna que mostrar.',
};

/** El expediente se leyó y no tiene ni el frente ni el reverso de la identificación. */
export const NO_ID_DOCUMENT: Gap = {
    label: 'Sin identificación en expediente',
    note: 'La lectura del expediente salió bien y no trae ningún lado de la identificación de este cliente: su verificación nunca llegó a subir el documento.',
};

/** La autorización de buró se firma con el contrato del crédito: sin crédito no hay qué firmar. */
export const NO_CREDIT_TO_AUTHORIZE: Gap = {
    label: 'Sin crédito que autorizar',
    note: 'La autorización de consulta al SIC se firma junto con el contrato del crédito, y este cliente no tiene ninguno. No es que se haya negado a firmarla.',
};

/* ================================================================================
   DOCUMENTOS DE ONBOARDING — cuelgan del directorio de Fiado, no del crédito.
   ================================================================================ */

/** La lectura no falló y tampoco trajo cuerpo. No es un expediente vacío ni una lectura fallida. */
export const DOCUMENTS_WITHOUT_BODY: Gap = {
    label: 'La respuesta llegó sin cuerpo',
    note: 'La lectura de los documentos no falló y aun así volvió sin nada dentro. No se puede decir si este cliente tiene expediente: lo que llegó no trae ni la lista vacía.',
};

/** El archivo existe y su enlace no se pudo firmar: no es lo mismo que no existir. */
export const DOCUMENT_LINK_UNSIGNED: Gap = {
    label: 'El enlace no se pudo firmar',
    note: 'El archivo está en el expediente y el enlace temporal para abrirlo no se pudo generar. El documento existe: lo que falló fue la firma del enlace, y se puede reintentar.',
};

/* ================================================================================
   CONTRATOS DEL CRÉDITO — el catálogo del silo emite tres documentos y ninguno es la carátula.
   ================================================================================ */

/** El enum de contratos tiene tres tipos y ninguno es la carátula. */
export const CAT_COVER_NO_SOURCE: Gap = {
    label: 'No existe en el catálogo',
    note: 'La carátula con el costo anual total no existe: el catálogo de contratos del silo emite tres documentos —contrato de crédito, pagaré y autorización de consulta al SIC— y ninguno es esa carátula.',
};

/* ================================================================================
   ARCO — el módulo no existe. El vacío del diseño diría lo contrario.
   ================================================================================ */

/**
 * El vacío del diseño dice «este cliente no ha ejercido derechos ARCO». Sería falso: nadie podría
 * ejercerlos aunque quisiera, porque no hay dónde registrarlos.
 */
export const ARCO_MODULE_PENDING_TITLE = 'Módulo pendiente';

export const ARCO_MODULE_PENDING_NOTE =
    'El registro de solicitudes ARCO —acceso, rectificación, cancelación y oposición— está diseñado en papel y no construido: no hay tabla, ni endpoint, ni pantalla donde se capturen. Esta ficha no puede decir que el cliente no ejerció sus derechos, porque hoy nadie podría ejercerlos.';

/* ================================================================================
   HUECOS LEÍDOS — el dato llegó vacío, y la leyenda dice qué significa que lo esté.
   ================================================================================ */

export const NO_CURP: Gap = {
    label: 'Sin CURP',
    note: 'El alta de este cliente no registró CURP. La escribe el KYC cuando lee el documento, así que un cliente sin verificar puede no tenerla.',
};

export const NO_RFC_BASE: Gap = {
    label: 'Sin RFC base',
    note: 'El RFC base se deriva de la CURP y este cliente no tiene CURP registrada. El silo guarda un campo de RFC aparte, pero ningún flujo lo escribe.',
};

/** Una sola leyenda para el hueco: el hero y la pestaña Perfil describen la MISMA ausencia. */
export const NO_ORIGIN_STORE: Gap = {
    label: 'Sin tienda de alta',
    note: 'La ficha de este cliente no guarda en qué tienda se dio de alta. Es un campo obligatorio del silo, así que su ausencia es un dato viejo, no un cliente sin tienda.',
};

/**
 * La lectura del directorio SALIÓ BIEN y la tienda volvió sin nombre. Decir «sin tienda de alta»
 * acusaría al alta de no registrar algo que sí registró: lo que falta es del lado del directorio.
 */
export const NO_ORIGIN_STORE_NAME: Gap = {
    label: 'El directorio no la nombra',
    note: 'La tienda donde se dio de alta a este cliente está en el directorio del negocio y su registro volvió sin nombre. El alta sí la guardó: el hueco es del directorio, y se cierra nombrando esa tienda.',
};

export const NO_CREATED_AT: Gap = {
    label: 'Sin fecha de alta',
    note: 'La ficha no trae el día en que este cliente entró al padrón. El silo la escribe al crear el registro, así que su ausencia es un registro viejo y no un alta reciente.',
};
